export const reactNotes = [
  {
    id: 101,
    topic: "react",
    seriesKey: "react",
    title: "React  History & Developer Info",
    description:
      "Learn about React's origins, its creator Jordan Walke, and the timeline of its evolution.",
    content: `
##  Who developed React?
 **Creator:** *Jordan Walke* (Software Engineer at Facebook)
 **First Release:** *March 2013* (open-sourced by Facebook at JSConf US)
**Company:** Originally developed at Facebook (now Meta)
##  History Timeline
 **2011:** Jordan Walke created FaxJS, the prototype of React.
 **2011:** React used in Facebook's News Feed.
 **2012:** React adopted in Instagram.
 **2013:** Open-sourced at JSConf US.
 **2015:** React Native launched for mobile apps.
 **2017:** React Fiber (new reconciliation engine) introduced.
 **2019+:** Hooks API introduced (shift from class → functional components).
 **2022+:** Focus on Concurrent Rendering & Server Components.
##  Why React became popular?
- Component-based architecture (reusable UI blocks).
- Virtual DOM for efficient UI updates.
- Backed by Facebook/Meta & widely adopted at scale.
- Huge community and ecosystem.
  `,
    tags: ["React", "History", "Developer", "Meta"],
    date: "2024-10-01",
  },
  {
    id: 102,
    topic: "react",
    seriesKey: "react",
    title: "What is React? Introduction to the Library",
    description:
      "Understand React as a declarative, component-based JavaScript library for building user interfaces.",
    content: `
##  What is React?
React is a **JavaScript library** for building interactive UIs. It focuses on rendering components efficiently and managing UI state.
### Key Characteristics:
 **1.Library, not Framework:** React manages only the view layer. Developers can add their own tools for routing, state, etc.
 **2.Component-Based:** Build UIs using encapsulated pieces (components).
 **3.Declarative UI:** Describe *what* UI should look like, React updates DOM automatically.
 **4.Cross-Platform:** React DOM (web), React Native (mobile), React VR (3D/VR apps).
###  Why React?
- Speeds up development.
- Easy to debug.
- Encourages code reusability.
  `,
    tags: ["React", "Introduction", "Concepts"],
    date: "2024-10-01",
  },
  {
    id: 103,
    topic: "react",
    seriesKey: "react",
    title: "Introduction to React: SPA Concept",
    description:
      "Understand what a Single Page Application (SPA) is and how React helps in building them.",
    content: `
##  Single Page Applications (SPA)
A **Single Page Application** is a web app that loads a single HTML page and dynamically rewrites the content as the user interacts with the app.
### How SPAs Work:
 **1.Initial Load:** The browser loads a single HTML file (usually \`index.html\`) and the necessary JavaScript/CSS.
 **2.Client-Side Routing:** When the user clicks a link, the URL changes, but the browser does not request a new HTML page from the server.
**3.Dynamic Rendering:** JavaScript (React) intercepts the request and dynamically renders the new view/content into the existing page structure.
###  Benefits of SPA with React:
 **Fast User Experience:** Once loaded, transitions are quick and smooth.
 **Better Performance:** Only data (JSON) is fetched for updates, not entire HTML pages.
 **Separation of Concerns:** Clear separation between the backend API and the frontend UI.
    `,
    tags: ["React", "Introduction", "SPA", "Concept"],
    date: "2024-10-02",
  },
  {
    id: 104,
    topic: "react",
    seriesKey: "react",
    title: "JSX: Syntax and Expressions",
    description:
      "Learn the fundamentals of JSX, React's syntax extension, for describing UI structure.",
    content: `
##  What is JSX?
**JSX** (JavaScript XML) is a syntax extension to JavaScript. It allows you to write HTML-like structures directly within your JavaScript code.
### Key Rules:
 * Single Root Element: JSX elements must be wrapped by a single parent element.
 * CamelCase for Attributes: HTML attributes like \`class\` become \`className\` and \`for\` becomes \`htmlFor\`.
* Self-Closing Tags: Tags like \`<img>\` and \`<br>\` must be closed: \`<img />\` or \`<br />\`.
###  Embedding Expressions
You can embed any valid JavaScript expression inside JSX using curly braces **\`{ }\`**.
\`\`\`jsx
const name = "Alice";
const element = <h1>Hello, {name.toUpperCase()}!</h1>;
// Renders: <h1>Hello, ALICE!</h1>
\`\`\`
### React Fragments
* Fragments (\`<React.Fragment>\` or the short syntax \`<>...</>\`) allow you to group multiple JSX elements without adding an extra node to the DOM.
\`\`\`jsx
function ChildComponent() {
  return (
    <>
      <p>Part one</p>
      <p>Part two</p>
    </>
  );
}
\`\`\`
    `,
    tags: ["React", "JSX", "Syntax", "Expressions", "Fragments"],
    date: "2024-10-02",
  },
  {
    id: 105,
    topic: "react",
    seriesKey: "react",
    title: "Functional Components: The Building Blocks of React",
    description:
      "A deep dive into functional components, the modern standard for writing React UI.",
    content: `
##  Functional Components
Functional components are JavaScript functions that accept an object of \`props\` (properties) as an argument and return a React element (JSX).
### Basic Structure:
\`\`\`jsx
import React from 'react';

// Arrow function syntax (most common)
const Welcome = (props) => {
  return <h1>Hello, {props.name}</h1>;
}

// Traditional function syntax
function Goodbye(props) {
    return <p>Goodbye!</p>;
}

export default Welcome;
\`\`\`
###  Advantages over Class Components:
 **Simpler to Read and Write:** Less boilerplate code.
 **Hooks API:** Functional components utilize Hooks for state and lifecycle management, which simplifies component logic.
 **Better Performance:** Can sometimes result in smaller bundle sizes and minor performance benefits (though the React engine treats them similarly).
    `,
    tags: ["React", "Components", "Functional", "JSX"],
    date: "2024-10-03",
  },
  {
    id: 106,
    topic: "react",
    seriesKey: "react",
    title: "Props (Properties) in React",
    description:
      "Understand the concept of props for passing data uni-directionally from parent to child components.",
    content: `
##  Props (Properties)
**Props** is a special object in React that holds data passed from a parent component to a child component. They are **read-only**.
### Key Characteristics:
 **Uni-directional Data Flow:** Data moves only down the component tree (Parent → Child).
**Immutable:** A child component should never modify the props it receives.
### Example: Passing Props
\`\`\`jsx
// Parent Component
function App() {
  return <Profile name="Charlie" age={30} />;
}

// Child Component
function Profile(props) {
  // props object: { name: "Charlie", age: 30 }
  return (
    <div>
      <h2>{props.name}</h2>
      <p>Age: {props.age}</p>
    </div>
  );
}
\`\`\`
### Destructuring Props
It's common practice to destructure props for cleaner code:
\`\`\`jsx
function Profile({ name, age }) { // Destructuring in the function signature
  return (
    <div>
      <h2>{name}</h2>
      <p>Age: {age}</p>
    </div>
  );
}
\`\`\`
    `,
    tags: ["React", "Props", "Data Flow", "Components"],
    date: "2024-10-03",
  },
  {
    id: 107,
    topic: "react",
    seriesKey: "react",
    title: "Children Props: Passing Content via the Component Body",
    description:
      "How to use the special 'children' prop to render nested JSX passed between component tags.",
    content: `
##  Children Props
The \`children\` prop is a special prop automatically passed to components that allows you to render the content placed between the component's opening and closing tags.
### Use Case: Wrapper Components
It's perfect for creating layout or wrapper components like cards, modals, or page containers.
### Example:
\`\`\`jsx
// 1. Wrapper Component (Parent)
function Card({ title, children }) {
  return (
    <div className="card">
      <h3>{title}</h3>
      {/* Renders whatever is inside the Card tags */}
      <div className="card-content">{children}</div>
    </div>
  );
}

// 2. Usage in another Component
function App() {
  return (
    <Card title="User Profile">
      {/* This paragraph is the 'children' prop */}
      <p>This is the detailed profile content.</p>
    </Card>
  );
}
\`\`\`
    `,
    tags: ["React", "Props", "Children", "Wrapper"],
    date: "2024-10-04",
  },
  {
    id: 108,
    topic: "react",
    seriesKey: "react",
    title: "State Management (Local State) with useState",
    description:
      "Introduction to local state and the useState Hook for managing component-specific data.",
    content: `
##  Local State
**State** is data that changes over time, affecting the rendering of a component. **Local State** is data managed *within* a single component.
### The \`useState\` Hook
The primary way to manage state in functional components is the \`useState\` Hook.
\`\`\`jsx
import React, { useState } from 'react';

function Counter() {
  // useState returns an array with:
  // 1. The current state value (count)
  // 2. A function to update the state (setCount)
  const [count, setCount] = useState(0); 

  const increment = () => {
    // We use the setter function to update the state
    setCount(count + 1);
  };

  return (
    <div>
      <p>Count: {count}</p>
      <button onClick={increment}>Increment</button>
    </div>
  );
}
\`\`\`
### Initial State
The value passed to \`useState()\` (e.g., \`0\`) is the initial state value. This can be any valid JavaScript type (number, string, boolean, object, array).
    `,
    tags: ["React", "State", "Hooks", "useState", "Local State"],
    date: "2024-10-04",
  },
  {
    id: 109,
    topic: "react",
    seriesKey: "react",
    title: "Updating State & State Batching",
    description:
      "Best practices for updating state, especially when it depends on the previous value, and how React batches updates.",
    content: `
##  Updating State
### 1. Simple Update (Direct Value)
Use this when the new state does **not** depend on the previous state.
\`\`\`javascript
setCount(10); // Set count directly to 10
\`\`\`
### 2. Functional Update (Based on Previous State)
Use this when the new state **depends** on the previous state value. This guarantees you are working with the most up-to-date state.
\`\`\`javascript
// This is the correct way to increment:
setCount(prevCount => prevCount + 1);
\`\`\`
##  State Batching
React often groups (batches) multiple state updates into a single re-render for performance.
 **Pre-React 18:** Batching only happened for updates inside React event handlers (like \`onClick\`).
 **React 18 (Automatic Batching):** React now automatically batches state updates, even across asynchronous code like promises, \`setTimeout\`, and native event handlers, whenever possible.
\`\`\`javascript
function handleClick() {
  setCount(c => c + 1); // Batched
  setIsLoading(true);   // Batched
  // Only one re-render happens for both updates
}
\`\`\`
    `,
    tags: ["React", "State", "useState", "Batching", "Performance"],
    date: "2024-10-05",
  },
  {
    id: 110,
    topic: "react",
    seriesKey: "react",
    title: "Event Handling in React",
    description:
      "Learn how to handle user events like clicks, form submissions, and key presses in React components.",
    content: `
##  Event Handling
React's event handling system is very similar to native HTML events but uses **synthetic events** and **camelCase** naming.
### 1. Attaching Handlers
Event handlers are attached directly to the JSX element using camelCase (e.g., \`onClick\`, \`onChange\`).
\`\`\`jsx
function Button() {
  function handleClick() {
    console.log('Button clicked!');
  }

  return (
    // 'handleClick' is passed as a function reference
    <button onClick={handleClick}>Click Me</button>
  );
}
\`\`\`
### 2. SyntheticEvent
React wraps native browser events into a **SyntheticEvent** object. It has the same interface as a native browser event but works consistently across all browsers.
### 3. Preventing Default Behavior
To prevent default browser behavior (like a form submitting and refreshing the page), use \`event.preventDefault()\` inside the handler.
\`\`\`jsx
function handleSubmit(event) {
  event.preventDefault(); // Stops page refresh
  // ... form submission logic
}
\`\`\`
    `,
    tags: ["React", "Events", "onClick", "SyntheticEvent"],
    date: "2024-10-05",
  },
  {
    id: 111,
    topic: "react",
    seriesKey: "react",
    title: "Passing Event Handlers as Props",
    description:
      "How to use event handlers as a callback mechanism for a child component to communicate back to its parent.",
    content: `
##  Passing Handlers as Props (Callbacks)
Since props flow down, the standard way for a child component to notify its parent of an event is for the parent to pass a function (the handler) down as a prop.
### Steps:
 **1.Parent:** Define the function that should run when the event occurs.
 **2.Parent:** Pass this function to the child as a prop (e.g., \`onSave\`, \`onDelete\`).
 **3.Child:** Call the function prop inside its own event handler.
### Example:
\`\`\`jsx
// Parent Component
function App() {
  const handleSave = (data) => {
    console.log('Saved data:', data); // Parent receives data
  };

  // Pass the function as the 'onSave' prop
  return <SaveButton onSave={handleSave} />;
}

// Child Component
function SaveButton({ onSave }) {
  const dataToSave = { id: 1, text: 'New Note' };

  return (
    <button onClick={() => onSave(dataToSave)}> 
      Save Note
    </button>
  );
}
\`\`\`
    `,
    tags: ["React", "Events", "Props", "Callback", "Communication"],
    date: "2024-10-06",
  },
  {
    id: 112,
    topic: "react",
    seriesKey: "react",
    title: "Conditional Rendering in React",
    description:
      "Techniques for rendering different UI elements based on certain conditions (state, props, etc.).",
    content: `
##  Conditional Rendering
Conditional rendering allows you to display different elements or components based on a condition (usually a boolean state or prop).
### 1. JavaScript \`if/else\`
The standard \`if/else\` statement is used *outside* of the \`return\` statement.
\`\`\`jsx
function Greeting({ isLoggedIn }) {
  if (isLoggedIn) {
    return <h1>Welcome back!</h1>;
  } else {
    return <button>Login</button>;
  }
}
\`\`\`
### 2. Ternary Operator
The **Ternary Operator** (\`condition ? true : false\`) is excellent for simple, inline conditional logic inside JSX.
\`\`\`jsx
function App({ isAdmin }) {
  return (
    <div>
      {isAdmin ? <AdminPanel /> : <UserPanel />}
    </div>
  );
}
\`\`\`
### 3. Logical && Operator
The **Logical AND** operator (\`&&\`) is used when you want to render *something* if the condition is true, and *nothing* if it is false.
\`\`\`jsx
// If 'unreadMessages' is > 0, the element is rendered.
function Mailbox({ unreadMessages }) {
  return (
    <div>
      {unreadMessages.length > 0 && 
        <h2>You have {unreadMessages.length} unread messages.</h2>
      }
    </div>
  );
}
\`\`\`
    `,
    tags: ["React", "Conditional Rendering", "Ternary", "Logic"],
    date: "2024-10-06",
  },
  {
    id: 113,
    topic: "react",
    seriesKey: "react",
    title: "Lists & Keys",
    description:
      "How to dynamically render lists of data using the array map function and the critical importance of the 'key' prop.",
    content: `
##  Rendering Lists
In React, you render lists of elements using the standard JavaScript **\`map()\`** array method inside JSX.
### Example:
\`\`\`jsx
const fruits = ['Apple', 'Banana', 'Cherry'];

function FruitList() {
  return (
    <ul>
      {fruits.map((fruit, index) => (
        <li key={index}>{fruit}</li> 
        // NOTE: The 'key' prop is required here
      ))}
    </ul>
  );
}
\`\`\`
##  Keys in React
The \`key\` prop is a special string attribute you **must** include when creating list elements.
### Importance of Keys:
**Identity:** Keys help React identify which items have changed, are added, or are removed.
 **Performance:** Keys allow React to efficiently reuse and reorder elements rather than re-rendering the entire list.
 **Stable Identity:** React uses keys to maintain the state of components/elements.
### Best Practice for Keys:
 **1.Use Unique IDs:** Always use a stable, unique ID from your data (e.g., database ID).
**2.Avoid Array Index:** Only use the array index as a key if the list is **static** and will **never be reordered, filtered, or added to**. Using indexes for dynamic lists can lead to bugs and performance issues.
    `,
    tags: ["React", "Lists", "map", "Keys", "Performance"],
    date: "2024-10-07",
  },
  {
    id: 114,
    topic: "react",
    seriesKey: "react",
    title: "Controlled vs Uncontrolled Components",
    description:
      "An overview of how React manages form data and the difference between controlled and uncontrolled components.",
    content: `
##  Forms and User Input
### 1. Controlled Components (Preferred Method)
In a **Controlled Component**, the form data is handled by the component's state. The component is responsible for controlling and maintaining the input's current value.
 **Data Flow:** State is the single source of truth.
**How it works:** You use the \`value\` prop to set the input's value and the \`onChange\` event handler to update the state with the user's input.
\`\`\`jsx
function NameForm() {
  const [name, setName] = useState('');

  const handleChange = (event) => {
    setName(event.target.value);
  };

  return (
    <input type="text" value={name} onChange={handleChange} />
  );
}
\`\`\`
### 2. Uncontrolled Components
In an **Uncontrolled Component**, the form data is handled by the DOM itself (like standard HTML forms). React accesses the values only when needed, usually through a **\`useRef\`** Hook on form submission.
 **Data Flow:** DOM is the source of truth.
 **Use Case:** Simpler forms or integrating with third-party, non-React DOM libraries.
    `,
    tags: ["React", "Forms", "Input", "Controlled", "Uncontrolled", "State"],
    date: "2024-10-07",
  },
  {
    id: 115,
    topic: "react",
    seriesKey: "react",
    title: "Styling in React",
    description:
      "A look at common methods for styling React components, focusing on built-in and modular techniques.",
    content: `
##  Styling React Components
### 1. Inline Styles
You can pass a JavaScript object as the \`style\` prop. Style properties are written in **camelCase**.
\`\`\`jsx
const styleObject = {
  backgroundColor: 'blue', // Note the camelCase
  fontSize: '16px',
  padding: '10px'
};

function MyComponent() {
  return (
    <h1 style={styleObject}>Hello</h1>
  );
}
\`\`\`
### 2. CSS Modules (Recommended for Standard CSS)
CSS Modules ensure that CSS is scoped locally to the component, preventing style conflicts.
 **Naming Convention:** \`[ComponentName].module.css\`
 **Usage:** Import the CSS file as an object, and reference classes as properties of that object.
\`\`\`jsx
// App.module.css
.header {
  color: red;
}

// App.jsx
import styles from './App.module.css';

function App() {
  // The className will be uniquely generated (e.g., 'App_header__abc12')
  return <h1 className={styles.header}>Styled Header</h1>; 
}
\`\`\`
### 3. External Libraries
 **Styled-components:** Uses tagged template literals for writing CSS directly in JavaScript, providing powerful dynamic styling.
 **Tailwind CSS:** A utility-first CSS framework that applies styling via classes (e.g., \`class="text-xl font-bold"\`).
    `,
    tags: ["React", "Styling", "Inline", "CSS Modules", "Tailwind"],
    date: "2024-10-08",
  },
  {
    id: 116,
    topic: "react",
    seriesKey: "react",
    title: "Core Hooks: The useState Hook",
    description:
      "A quick review of the useState hook for managing local component state.",
    content: `
##  Core Hook: useState (State Hook)
The \`useState\` hook lets you add state variables to functional components.
### Syntax:
\`\`\`javascript
const [state, setState] = useState(initialState);
\`\`\`
 **\`state\`**: The current value of the state.
 **\`setState\`**: The function to update the state.
 **\`initialState\`**: The value the state starts with.
### Functional Update Review:
Always use the functional form of the setter when the new state relies on the previous state:
\`\`\`javascript
// Correct way to toggle a boolean
setIsToggled(prevToggled => !prevToggled); 
\`\`\`
    `,
    tags: ["React", "Hooks", "useState", "Core Hooks", "State"],
    date: "2024-10-08",
  },
  {
    id: 117,
    topic: "react",
    seriesKey: "react",
    title: "Core Hooks: The useEffect Hook",
    description:
      "Detailed explanation of useEffect for handling side effects like data fetching, subscriptions, and manual DOM manipulation.",
    content: `
##  Core Hook: useEffect (Effect Hook)
The \`useEffect\` hook lets you perform **side effects** in functional components. A side effect is anything that reaches outside the component, such as:
- Data fetching (API calls)
- Subscriptions (setting up and cleaning up listeners)
- Manually changing the DOM
### Syntax and Cleanup:
\`\`\`javascript
useEffect(() => {
  // --- Side Effect Code Goes Here ---
  document.title = 'New Title'; 

  // --- Optional Cleanup Function ---
  return () => {
    // This runs when the component unmounts OR before the effect runs again.
    document.title = 'React App'; // Cleanup example
  };
}, [dependencies]); // Dependency Array
\`\`\`
### The Dependency Array (\`[]\`)
This second argument controls *when* the effect runs:
 **1.No Array:** Runs after *every* render. (Rarely used)
 **2.Empty Array (\`[]\`):** Runs only **once** after the initial render (mimics \`componentDidMount\`).
 **3.With Values (\`[propA, stateB]\`):** Runs after the initial render AND whenever any value in the array changes.
    `,
    tags: ["React", "Hooks", "useEffect", "Side Effects", "Lifecycle"],
    date: "2024-10-09",
  },
  {
    id: 118,
    topic: "react",
    seriesKey: "react",
    title: "Core Hooks: The useRef Hook",
    description:
      "Learn how to use useRef to create mutable references that persist across renders without causing component re-renders.",
    content: `
##  Core Hook: useRef (Ref Hook)
The \`useRef\` hook returns a mutable ref object whose \`.current\` property is initialized to the passed argument. Crucially, it persists its value across the lifetime of the component.
### Key Use Cases:
 **1.Accessing the DOM:** Getting a direct reference to a DOM element (e.g., focusing an input).
 **2.Storing Mutable Values:** Holding a value that should persist across renders but **not** trigger a re-render when it changes (e.g., timers, previous state value).
### 1. Accessing the DOM:
\`\`\`jsx
function MyInput() {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current.focus(); // Focuses the input element
  }, []); // Runs once

  return <input ref={inputRef} type="text" />;
}
\`\`\`
### 2. Storing a Mutable Value:
\`\`\`javascript
const intervalRef = useRef(null); // Used to hold a timer ID
\`\`\`
    `,
    tags: ["React", "Hooks", "useRef", "Refs", "DOM"],
    date: "2024-10-09",
  },
  {
    id: 119,
    topic: "react",
    seriesKey: "react",
    title: "Core Hooks: The useContext Hook",
    description:
      "How to use useContext to consume values from the Context API, avoiding prop drilling.",
    content: `
##  Core Hook: useContext (Context Hook)
The **Context API** is React's built-in solution for passing data through the component tree without having to manually pass props down at every level (**prop drilling**). The \`useContext\` hook is the component that reads that data.
### Steps to Use Context:
1. **Create:** Use \`React.createContext()\` to create a Context object.
2. **Provide:** Use the \`Context.Provider\` component higher up the tree to make the value available.
3. **Consume (Read):** Use the \`useContext(ContextObject)\` Hook in any descendant component.
### Example (Consumption):
\`\`\`jsx
// 1. Assume 'ThemeContext' was created and provided higher up
import { ThemeContext } from './ThemeContext'; 

function Button() {
  // 2. Consume the context value
  const theme = useContext(ThemeContext); 
  
  return (
    <button style={{ background: theme.background }}>
      Themed Button
    </button>
  );
}
\`\`\`
    `,
    tags: ["React", "Hooks", "useContext", "Context API", "Prop Drilling"],
    date: "2024-10-10",
  },
  {
    id: 120,
    topic: "react",
    seriesKey: "react",
    title: "Advanced Hooks: The useReducer Hook",
    description:
      "An in-depth look at useReducer, often used as an alternative to useState for complex state logic or when state transitions are complex.",
    content: `
##  Advanced Hook: useReducer
The \`useReducer\` hook is often used as an alternative to \`useState\` when you have complex state logic that involves multiple sub-values or when the next state depends on the previous one. It's conceptually similar to Redux.
### Syntax:
\`\`\`javascript
const [state, dispatch] = useReducer(reducer, initialState, initFunction);
\`\`\`
 **\`reducer\`:** A function that takes the current \`state\` and an \`action\` and returns the new state.
 **\`state\`:** The current state value.
 **\`dispatch\`:** A function used to send (dispatch) actions to the reducer.
### Example:
\`\`\`javascript
const initialState = { count: 0 };

function reducer(state, action) {
  switch (action.type) {
    case 'increment':
      return { count: state.count + 1 };
    case 'reset':
      return initialState;
    default:
      return state;
  }
}

function Counter() {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <>
      Count: {state.count}
      <button onClick={() => dispatch({ type: 'increment' })}>Increment</button>
      <button onClick={() => dispatch({ type: 'reset' })}>Reset</button>
    </>
  );
}
\`\`\`
    `,
    tags: ["React", "Hooks", "useReducer", "State Management", "Advanced"],
    date: "2024-10-10",
  },
  {
    id: 121,
    topic: "react",
    seriesKey: "react",
    title: "Advanced Hooks: useMemo ",
    description:
      "How to use useMemo to cache expensive calculations and prevent them from running on every re-render.",
    content: `
##  Advanced Hook: useMemo
The \`useMemo\` hook is used for **memoization**, which is a technique that stores the result of an expensive function call and reuses it when the same inputs occur. It optimizes performance by preventing unnecessary re-calculations.
### Syntax:
\`\`\`javascript
const memoizedValue = useMemo(() => {
  // Expensive calculation function here
  return calculateExpensiveValue(a, b); 
}, [a, b]); // Dependency Array
\`\`\`
### How it Works:
- The function inside \`useMemo\` only runs if one of the dependencies in the array (\`[a, b]\`) has changed.
- If the dependencies are the same, React skips running the function and returns the cached \`memoizedValue\`.
### Key Use Case:
- Calculating a large, filtered list.
- Deep object/array transformations.
- Any time a calculation takes noticeable time (e.g., > 1ms).

 **Caution:** Only use \`useMemo\` for genuinely expensive calculations, as memoization itself has a small overhead.
    `,
    tags: [
      "React",
      "Hooks",
      "useMemo",
      "Memoization",
      "Performance",
      "Optimization",
    ],
    date: "2024-10-11",
  },
  {
    id: 122,
    topic: "react",
    seriesKey: "react",
    title: "Advanced Hooks: useCallback",
    description:
      "The useCallback hook for memoizing functions to prevent unnecessary re-creation and optimize child component rendering.",
    content: `
##  Advanced Hook: useCallback
The \`useCallback\` hook is used to **memoize an entire function** definition. This means the function object will only be re-created if one of its dependencies changes.
### Syntax:
\`\`\`javascript
const memoizedCallback = useCallback(() => {
  doSomething(a, b); 
}, [a, b]); // Dependency Array
\`\`\`
### Why Memoize Functions?
In JavaScript, a function created inside a component is re-created on every re-render. This causes issues when:
 **1.Passing to \`useEffect\`:** If a function is a dependency of \`useEffect\`, the effect will re-run unnecessarily.
 **2.Passing to Memoized Children:** If you pass a new function reference as a prop to a child component wrapped in \`React.memo\`, the child will re-render unnecessarily because the prop (\`the function\`) is technically new every time.
**\`useCallback\` solves problem #2** by ensuring the function reference remains the same unless its internal dependencies change.
 **Key Distinction:** \`useMemo\` caches the **result** of a function call. \`useCallback\` caches the **function itself**.
    `,
    tags: [
      "React",
      "Hooks",
      "useCallback",
      "Memoization",
      "Performance",
      "Optimization",
    ],
    date: "2024-10-11",
  },
  {
    id: 123,
    topic: "react",
    seriesKey: "react",
    title: "Advanced Hooks: Custom Hooks",
    description:
      "How to create your own reusable hooks to share stateful logic across multiple components.",
    content: `
##  Custom Hooks
A **Custom Hook** is simply a JavaScript function whose name starts with **\`use\`** (e.g., \`useFetchData\`, \`useLocalStorage\`) and may call other Hooks (like \`useState\`, \`useEffect\`).
### Purpose:
- To extract and reuse stateful logic from components.
- To share non-visual behavior across the application.
### Rule of Hooks:
The only place you can call a custom hook is from the top level of a functional component or another custom hook.
### Example: A Simple Custom Hook
\`\`\`javascript
// useToggle.js
import { useState } from 'react';

// Convention: starts with 'use'
function useToggle(initialValue = false) {
  const [isToggled, setIsToggled] = useState(initialValue);

  const toggle = () => {
    setIsToggled(prev => !prev);
  };

  // Expose the state and the updater function
  return [isToggled, toggle]; 
}

// Usage in a Component
function LightSwitch() {
  const [isOn, toggle] = useToggle(false);
  return (
    <button onClick={toggle}>
      {isOn ? 'ON' : 'OFF'}
    </button>
  );
}
\`\`\`
    `,
    tags: ["React", "Hooks", "Custom Hooks", "Logic", "Reusability"],
    date: "2024-10-12",
  },
  {
    id: 124,
    topic: "react",
    seriesKey: "react",
    title: "Component Communication",
    description:
      "A summary of the primary methods for passing data between different components in a React application.",
    content: `
##  Component Communication Methods
### 1. Parent to Child: Props
 * Mechanism: The parent passes data as attributes (\`props\`) to the child component.
 * Example: \`<Child name="Alice" />\`
### 2. Child to Parent: Callbacks
 * Mechanism: The parent defines a function and passes it down to the child as a prop. The child calls this function when an event occurs, optionally passing data back up.
 *   Example: \`<Child onUpdate={handleUpdate} />\` (Parent defines \`handleUpdate\`, child calls \`props.onUpdate(data)\`)
### 3. Sibling/Any Distance: Context API
 * Mechanism: Used to pass data to components deep in the tree without "prop drilling" (passing the prop through many intermediate components).
 * Setup: Needs a Provider high up to supply the value, and  useContext  in the descendant component to consume the value.
### 4. Sibling/Any Distance: External State Management
 * Mechanism: Using libraries like Redux, Zustand, or Jotai to store state globally outside the component tree. Components connect to the store to read/write data.
    `,
    tags: ["React", "Communication", "Props", "Callback", "Context"],
    date: "2024-10-12",
  },
  {
    id: 125,
    topic: "react",
    seriesKey: "react",
    title: "React Router",
    description:
      "Introduction to React Router for handling client-side routing and defining navigation.",
    content: `
## React Router (For Client-Side Routing)
React Router is the standard library for adding declarative routing to a React Single Page Application (SPA).
### Key Components:
 **1.\`BrowserRouter\`:** Wraps the entire application and uses the HTML5 history API to keep the UI in sync with the URL.
 **2.\`Routes\`:** A container that groups individual \`Route\` elements.
 **3.\`Route\`:** Maps a URL path to a specific component.
 **4.\`Link\`:** Used instead of a native \`<a>\` tag to navigate. It prevents a full page reload.
### Setup Example:
\`\`\`jsx
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';

function App() {
  return (
    <BrowserRouter>
      <nav>
        <Link to="/">Home</Link>
        <Link to="/about">About</Link>
      </nav>
      <Routes>
        {/* Maps URL '/' to the Home component */}
        <Route path="/" element={<Home />} /> 
        
        {/* Maps URL '/about' to the About component */}
        <Route path="/about" element={<About />} />
      </Routes>
    </BrowserRouter>
  );
}
\`\`\`
    `,
    tags: ["React", "Routing", "React Router", "Links", "Setup"],
    date: "2024-10-13",
  },
  {
    id: 126,
    topic: "react",
    seriesKey: "react",
    title: "Advanced Routing",
    description:
      "Advanced routing concepts for dynamic paths, hierarchical UIs, and access control.",
    content: `
##  Advanced Routing
### 1. URL Parameters
Used to capture dynamic segments of the URL, often for displaying details of a specific item. The \`useParams\` hook is used to read the values.
\`\`\`jsx
// Route Definition
<Route path="/users/:userId" element={<UserProfile />} />

// In UserProfile Component
import { useParams } from 'react-router-dom';
function UserProfile() {
  const { userId } = useParams(); // userId will be the value from the URL
  // ... fetch user data using userId
}
\`\`\`
### 2. Nested Routes
Routes can be defined hierarchically, allowing parent components to manage a part of the UI while child components handle specific sub-paths.
\`\`\`jsx
<Route path="/dashboard" element={<DashboardLayout />}>
  {/* The path is relative to the parent: /dashboard/settings */}
  <Route path="settings" element={<UserSettings />} /> 
  <Route path="profile" element={<UserProfile />} />
</Route>
\`\`\`
### 3. Protected Routes
Used to restrict access to a route based on authentication/authorization status, often achieved using a wrapper component.
\`\`\`jsx
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth(); // Custom hook

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />; // Redirect if not logged in
  }
  return children;
}

// Usage
<Route 
  path="/secret" 
  element={
    <ProtectedRoute>
      <SecretPage />
    </ProtectedRoute>
  } 
/>
\`\`\`
    `,
    tags: [
      "React",
      "Routing",
      "URL Params",
      "Nested Routes",
      "Protected Routes",
    ],
    date: "2024-10-13",
  },
  {
    id: 127,
    topic: "react",
    seriesKey: "react",
    title: "State Management: Context API",
    description:
      "How to set up the Context API by creating the Context object and implementing the Provider component.",
    content: `
##  State Management: Context API
The Context API is a tool for sharing data that can be considered "global" for a tree of React components.
### 1. Create Context
\`\`\`javascript
// ThemeContext.js
import React from 'react';

// Create a Context object with an optional default value
export const ThemeContext = React.createContext('light');
\`\`\`
### 2. The Provider Component
The Provider component is placed high in the component tree. All components *within* the Provider's render tree can access its value.
\`\`\`jsx
// App.js
import { ThemeContext } from './ThemeContext';

function App() {
  const [theme, setTheme] = useState('dark');

  return (
    // The 'value' prop is the data to be shared
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <Toolbar /> {/* Toolbar and its children can read 'theme' */}
    </ThemeContext.Provider>
  );
}
\`\`\`
### Note on Performance
Context updates can cause all consuming components to re-render. For high-frequency updates, external state libraries are usually better.
    `,
    tags: [
      "React",
      "State Management",
      "Context API",
      "Provider",
      "Global State",
    ],
    date: "2024-10-14",
  },
  {
    id: 128,
    topic: "react",
    seriesKey: "react",
    title: "State Management: Redux Toolkit",
    description:
      "An overview of Redux Toolkit, the recommended approach for large-scale, predictable global state management.",
    content: `
##  State Management: Redux Toolkit (RTK)
**Redux** provides a predictable state container for JavaScript apps. **Redux Toolkit (RTK)** is the official, opinionated set of tools that simplifies Redux development and eliminates boilerplate.
### Core Concepts:
**Store:** Holds the entire global application state (the single source of truth).
 **Reducer:** Functions that determine how the state changes in response to actions.
 **Action:** Plain JavaScript objects that describe *what* happened.
 **Dispatch:** The function used to send actions to the store.
 **Selector:** Functions used to extract specific data from the store.
### Key RTK Features:
 **\`configureStore\`:** Sets up the Redux store with sane defaults and good tooling.
 **\`createSlice\`:** The primary way to define reducers and actions in a single, simple object. Reduces boilerplate significantly.
### Simplified Setup with \`createSlice\`
\`\`\`javascript
import { createSlice } from '@reduxjs/toolkit';

const counterSlice = createSlice({
  name: 'counter',
  initialState: { value: 0 },
  reducers: {
    increment: state => {
      state.value += 1; // Redux Toolkit allows this "mutating" syntax (it's safe)
    },
    reset: state => {
      state.value = 0;
    }
  }
});

// Export the action creators and the reducer
export const { increment, reset } = counterSlice.actions;
export default counterSlice.reducer;
\`\`\`
    `,
    tags: ["React", "State Management", "Redux", "Redux Toolkit", "RTK"],
    date: "2024-10-14",
  },
  {
    id: 129,
    topic: "react",
    seriesKey: "react",
    title: "State Management: Other Libraries",
    description:
      "Introduction to modern, lightweight state management libraries as alternatives to Redux.",
    content: `
##  Lightweight State Libraries
### Zustand
Zustand is a fast, scalable, and bear-bones state-management solution that has a simplified flux architecture.
 **Approach:** Uses a single, simple custom hook to create a store.
 **Key Feature:** Very minimal boilerplate. You create the store and consume it with one line.
 **Syntax:**
\`\`\`javascript
import { create } from 'zustand';

const useBearStore = create(set => ({
  bears: 0,
  increasePopulation: () => set(state => ({ bears: state.bears + 1 })),
}));

function BearCounter() {
  const bears = useBearStore(state => state.bears); // Simple consumption
  return <h1>{bears} bears around here...</h1>;
}
\`\`\`
### Jotai
Jotai is a state management library that takes an **Atomic** approach. It is based on the idea of defining state as small, isolated pieces of data (atoms).
 **Approach:** State is defined as "atoms" that can be combined and reused.
 **Key Feature:** Minimal API, built for TypeScript, and highly performant.
**Use Case:** Great for complex, dependency-heavy state or when you only need to update small, isolated parts of the state tree.
    `,
    tags: ["React", "State Management", "Zustand", "Jotai", "Libraries"],
    date: "2024-10-15",
  },
  {
    id: 130,
    topic: "react",
    seriesKey: "react",
    title: "Data Fetching",
    description:
      "Fundamental approach to fetching data in React using useEffect, and managing the core loading, error, and data states.",
    content: `
##  Data Fetching Fundamentals
In React, data fetching is a **side effect** and is typically handled within the **\`useEffect\`** hook.
### Core States to Manage:
When fetching data, you must manage three essential pieces of state:
1. **\`data\`**: The successful result from the API.
2. **\`loading\`**: A boolean (usually \`true\`) while the request is in progress.
3. **\`error\`**: Any error object if the request fails.
### Example using \`fetch\`:
\`\`\`jsx
function PostList() {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch('/api/posts');
        const json = await response.json();
        setPosts(json);
      } catch (error) {
        console.error('Fetch failed:', error);
      } finally {
        setIsLoading(false); // Stop loading regardless of success/fail
      }
    }
    fetchData();
  }, []); // Empty dependency array: runs only on mount

  if (isLoading) return <p>Loading posts...</p>;
  
  // ... render posts
}
\`\`\`
### Axios
**Axios** is a popular promise-based HTTP client that offers automatic JSON data transformation, better error handling, and request/response interceptors, often preferred over the native \`fetch\` API.
    `,
    tags: [
      "React",
      "Data Fetching",
      "useEffect",
      "fetch",
      "axios",
      "Loading State",
    ],
    date: "2024-10-15",
  },
  {
    id: 131,
    topic: "react",
    seriesKey: "react",
    title: "Data Fetching with React Query",
    description:
      "An introduction to React Query (now TanStack Query) for declarative, efficient, and auto-managed data fetching.",
    content: `
##  Data Fetching: React Query
**React Query** (officially **TanStack Query**) is a library that provides hooks for fetching, caching, and updating asynchronous data in React. It's often referred to as a "server state management" library.
### Why Use React Query?
React Query handles the complex, non-visual tasks that you had to manually code with \`useState\` and \`useEffect\`:
**Caching:** Caches fetched data automatically.
 **Refetching:** Automatically refetches data in the background (e.g., when the user focuses the window).
 **Stale-while-revalidate:** Shows cached data instantly, then fetches new data in the background.
**Error/Loading Management:** Simplifies the states into a clean object.
### Example: The \`useQuery\` Hook
\`\`\`jsx
import { useQuery } from '@tanstack/react-query';

function Todos() {
  // Pass a unique key and a fetcher function
  const { data, isLoading, isError, error } = useQuery({ 
    queryKey: ['todos'], 
    queryFn: fetchTodos // function that returns a Promise
  });

  if (isLoading) return <span>Loading...</span>;
  if (isError) return <span>Error: {error.message}</span>;

  return (
    // ... render data
    <div>{data.map(todo => <p key={todo.id}>{todo.title}</p>)}</div>
  );
}
\`\`\`
    `,
    tags: [
      "React",
      "Data Fetching",
      "React Query",
      "TanStack Query",
      "Caching",
    ],
    date: "2024-10-16",
  },
  {
    id: 132,
    topic: "react",
    seriesKey: "react",
    title: "Error Handling",
    description:
      "How to gracefully handle errors that occur during rendering using the React Error Boundary feature.",
    content: `
##  Error Handling: Error Boundaries
**Error Boundaries** are React components that **catch JavaScript errors** anywhere in their child component tree, log those errors, and display a fallback UI instead of crashing the entire application.
### Limitations:
Error Boundaries **do not** catch errors in:
- Event handlers (use \`try/catch\` inside the handler).
- Asynchronous code (e.g., \`setTimeout\`, \`Promise\`).
- Server-side rendering (SSR).
- The Error Boundary component itself.
### Implementation:
An Error Boundary must be a **class component** and implement either or both of these lifecycle methods:
1. **\`static getDerivedStateFromError(error)\`:** Renders the fallback UI.
2. **\`componentDidCatch(error, info)\`:** Logs the error information.
\`\`\`jsx
// ErrorBoundary.js (Class Component)
class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log error to an external service
    console.error("ErrorBoundary caught error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>; // Fallback UI
    }
    return this.props.children;
  }
}

// Usage
function App() {
  return (
    <ErrorBoundary>
      <ProblematicComponent />
    </ErrorBoundary>
  );
}
\`\`\`
    `,
    tags: ["React", "Error Handling", "Error Boundary", "Fallback UI"],
    date: "2024-10-16",
  },
  {
    id: 133,
    topic: "react",
    seriesKey: "react",
    title: "Code Splitting & Lazy Loading",
    description:
      "Techniques for optimizing load time by splitting the application code into smaller bundles and loading them on demand.",
    content: `
##  Code Splitting (Lazy Loading)
**Code Splitting** is the process of breaking a large application bundle into smaller, chunks that can be loaded on demand (lazily). This is essential for improving initial page load time.
### 1. \`React.lazy()\`
 \`React.lazy\` lets you render a dynamic import as a regular component. It automatically loads the component only when it is rendered.
\`\`\`javascript
// Before:
// import AdminPanel from './AdminPanel'; 

// After (Lazy Load):
const AdminPanel = React.lazy(() => import('./AdminPanel'));
\`\`\`
### 2. \`Suspense\`
- The \`<Suspense>\` component is required to wrap any lazy-loaded component. While the component is being loaded, \`Suspense\` renders a temporary **fallback** UI (e.g., a loading spinner).

\`\`\`jsx
function App() {
  return (
    <Suspense fallback={<div>Loading Admin Panel...</div>}>
      <AdminPanel />
    </Suspense>
  );
}
\`\`\`
### Use Case:
- Lazy loading is most often used for route-based code splitting, where components for unused routes are only loaded when the user navigates to them.
    `,
    tags: [
      "React",
      "Performance",
      "Optimization",
      "Code Splitting",
      "Lazy Loading",
      "Suspense",
    ],
    date: "2024-10-17",
  },
  {
    id: 134,
    topic: "react",
    seriesKey: "react",
    title: "Performance Optimization",
    description:
      "Consolidated view of the three main memoization tools and when to use each one for performance gains.",
    content: `
##  Performance Optimization Tools
### 1. \`React.memo\` (Memoizing Components)
 **Purpose:** Prevents a functional component from re-rendering if its props have not changed (performs a shallow comparison of props).
 **Use Case:** Wrapping "pure" components that frequently re-render with the same props.
\`\`\`jsx
const MemoizedComponent = React.memo(MyComponent);
\`\`\`
### 2. \`useMemo\` (Memoizing Values)
**Purpose:** Caches the **result of a function** (a value) to avoid re-calculating it on every render.
 **Use Case:** Expensive calculations, filtering large arrays, or creating complex objects/arrays that are used as props for memoized children.
### 3. \`useCallback\` (Memoizing Functions)
 **Purpose:** Caches the **function definition** itself.
 **Use Case:** To maintain the same function reference across renders, primarily when passing callback functions as props to components wrapped in \`React.memo\` (to prevent the memoized child from re-rendering).
| Tool | What it Memoizes | When to Use |
| :--- | :--- | :--- |
| \`React.memo\` | Component Output | If a component re-renders unnecessarily. |
| \`useMemo\` | A Value | For expensive calculations. |
| \`useCallback\` | A Function | When passing functions to memoized child components. |
    `,
    tags: [
      "React",
      "Performance",
      "Optimization",
      "Memoization",
      "React.memo",
      "useMemo",
      "useCallback",
    ],
    date: "2024-10-17",
  },
  {
    id: 135,
    topic: "react",
    seriesKey: "react",
    title: "Portals & Advanced Rendering",
    description:
      "An explanation of React Portals for rendering children outside the component's normal DOM hierarchy.",
    content: `
##  React Portals
A **Portal** provides a way to render a child component into a DOM node that exists *outside* the DOM hierarchy of the parent component.
### Use Case:
- Portals are primarily used for components that need to visually "break out" of their parent's container, such as:
 **Modals**
 **Tooltips**
 **Popovers**
 **Notifications**
### Why Portals?
If a modal is rendered inside a parent component that has \`overflow: hidden\` or a low \`z-index\`, the modal might be clipped or hidden. Portals allow the component to be rendered at the document root (\`document.body\`) while maintaining its React context (state, props, event handlers).
### Implementation:
\`\`\`javascript
// 1. You must have a target DOM node (e.g., a div with id="modal-root")
//    outside the main React root.

// 2. Use ReactDOM.createPortal:
import ReactDOM from 'react-dom';

function Modal({ children }) {
  const modalRoot = document.getElementById('modal-root');

  return ReactDOM.createPortal(
    // The content to be rendered (the children)
    children, 
    // The target DOM element
    modalRoot 
  );
}
\`\`\`
    `,
    tags: ["React", "Portals", "Advanced Rendering", "Modals", "DOM"],
    date: "2024-10-18",
  },
  {
    id: 136,
    topic: "react",
    seriesKey: "react",
    title: "Portals & Advanced Rendering",
    description:
      "The purpose and benefits of React's StrictMode component for identifying potential problems in an application.",
    content: `
## React StrictMode
**\`StrictMode\`** is a tool for highlighting potential problems in an application. It does **not** render any visible UI itself. It only activates additional checks and warnings for its descendants.
### Key Checks Performed:
 **Identifying unsafe lifecycle methods** (legacy class components).
  **Warning about legacy string ref usage.**
 **Detecting unexpected side effects** (by intentionally double-invoking some functions like component bodies and \`useEffect\` cleanup/setup).
 **Warning about deprecated API usage** (e.g., legacy Context API).
### How to Use:
- Wrap the part of your application you want to inspect with \`<React.StrictMode>\`. It is often wrapped around the entire application in development mode.
\`\`\`jsx
import React from 'react';
import ReactDOM from 'react-dom/client';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  // The checks run against everything inside here
  <React.StrictMode> 
    <App />
  </React.StrictMode>
);
\`\`\`
 **Note:** StrictMode checks only run in **development mode** and have no impact on the production build or performance.
    `,
    tags: ["React", "StrictMode", "Debugging", "Development"],
    date: "2024-10-18",
  },
  {
    id: 137,
    topic: "react",
    seriesKey: "react",
    title: "Testing React",
    description:
      "Overview of the primary tools and philosophies for testing React components.",
    content: `
##  Testing React Applications
Testing is crucial for ensuring the quality and stability of a React application.
### 1. Jest (The Test Runner)
 **What it is:** A delightful JavaScript Testing Framework with a focus on simplicity. It is often included by default in Create React App.
 **Role:** Handles running the tests, providing assertion functions (\`expect()\`), and generating test reports.
 **Snapshot Testing:** Jest can save a "snapshot" of a rendered component's output and compare it against future renders to detect unexpected UI changes.
### 2. React Testing Library (RTL)
 **What it is:** A set of utilities for testing React components.
 **Philosophy:** RTL's guiding principle is: **The more your tests resemble the way users use your components, the more confidence they can give you.**
 **Focus:** It encourages querying the DOM the way a user would (by label, text content, accessibility role, etc.), rather than focusing on internal implementation details (state, props).
\`\`\`javascript
// Example of RTL philosophy
import { render, screen } from '@testing-library/react';

test('renders a button with the correct text', () => {
  render(<MyButton />);
  // Querying by text content, which is how a user finds the element
  const buttonElement = screen.getByRole('button', { name: /click me/i }); 
  expect(buttonElement).toBeInTheDocument();
});
\`\`\`
    `,
    tags: ["React", "Testing", "Jest", "RTL", "React Testing Library"],
    date: "2024-10-19",
  },
  {
    id: 138,
    topic: "react",
    seriesKey: "react",
    title: "TypeScript with React",
    description:
      "Guide to using TypeScript to define types for component props and state variables.",
    content: `
## TypeScript with React
TypeScript adds static typing to JavaScript, helping catch errors during development.
### 1. Typing Functional Components and Props
Use the generic types \`React.FC\` (Functional Component) or \`React.VFC\` (Void Functional Component) for your function component signature.
\`\`\`typescript
import React from 'react';

// 1. Define the shape of the props object
interface WelcomeProps {
  name: string;
  age?: number; // ? means optional
}

// 2. Use React.FC<PropsType>
const Welcome: React.FC<WelcomeProps> = ({ name, age }) => {
  return <h1>Hello, {name}! You are {age || 'unknown'} years old.</h1>;
};
\`\`\`
### 2. Typing State (useState)
The type of state is often inferred by the initial value, but it's good practice to explicitly define a union type if the state can be multiple things (e.g., \`null\` or a \`User\` object).
\`\`\`typescript
// The type is inferred as number
const [count, setCount] = useState(0); 

// Explicitly define a type that can be null or a string
const [error, setError] = useState<string | null>(null); 
\`\`\`
    `,
    tags: ["React", "TypeScript", "Typing", "Props", "State"],
    date: "2024-10-19",
  },
  {
    id: 139,
    topic: "react",
    seriesKey: "react",
    title: "TypeScript with React",
    description:
      "How to apply types to other essential hooks like useEffect, useRef, and useReducer.",
    content: `
## TypeScript: Typing React Hooks
### 1. Typing \`useRef\`
The \`useRef\` generic is usually defined as a union type of the DOM element it attaches to OR \`null\`. The initial value is usually \`null\`.
\`\`\`typescript
// For a button element, use HTMLButtonElement
const buttonRef = useRef<HTMLButtonElement | null>(null);

useEffect(() => {
  // TypeScript knows buttonRef.current is a button element here
  if (buttonRef.current) {
    buttonRef.current.classList.add('active');
  }
}, []);
\`\`\`
### 2. Typing \`useEffect\`
\`useEffect\` generally does not need explicit typing, as its parameters are inferred correctly.
### 3. Typing \`useReducer\`
\`useReducer\` requires defining types for the **State**, the **Action**, and the **Reducer function**.
\`\`\`typescript
interface State {
  count: number;
}

// Union type for possible actions
type Action = { type: 'increment' } | { type: 'decrement' };

const reducer = (state: State, action: Action): State => { 
  // ... logic
}

const [state, dispatch] = useReducer(reducer, { count: 0 });
\`\`\`
    `,
    tags: ["React", "TypeScript", "Typing", "Hooks", "useRef", "useReducer"],
    date: "2024-10-20",
  },
  {
    id: 140,
    topic: "react",
    seriesKey: "react",
    title: "React Patterns",
    description:
      "Detailed explanation of two powerful design patterns for creating flexible and reusable components.",
    content: `
##  React Design Patterns
### 1. Compound Components
This pattern allows multiple components to work together implicitly to manage state and behavior, providing a powerful, expressive API.
- Analogy: HTML elements like \`<select>\` and \`<option>\` or \`<form>\` and \`<input>\`. They only work together.
- Mechanism: Often implemented using the Context API within the parent (e.g., \`<Tabs>\`) to share state with the child components (e.g., \`<Tab.List>\`, \`<Tab.Panel>\`).
\`\`\`jsx
// The API is clean and readable
<Select>
  <Select.Button />
  <Select.Options />
</Select>
\`\`\`
### 2. Render Props
A design pattern where a component passes a function (the **render prop**) as a prop, and the receiving component uses that function to render content. This shares behavior without coupling components.
- Mechanism: The value of the render prop is a function that returns JSX.
- Example:
\`\`\`jsx
function MouseTracker({ render }) {
  // MouseTracker handles the logic (x, y state)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 }); 

  // ... event listeners update mousePos ...

  // It hands off the logic to the caller via the render prop
  return <div style={{ height: '100%' }}>{render(mousePos)}</div>;
}

// Usage (passing the function as the 'render' prop)
function App() {
  return (
    <MouseTracker render={({ x, y }) => (
      <h1>The mouse is at ({x}, {y})</h1>
    )} />
  );
}
\`\`\`
    `,
    tags: ["React", "Patterns", "Compound Components", "Render Props"],
    date: "2024-10-20",
  },
  {
    id: 141,
    topic: "react",
    seriesKey: "react",
    title: "React Patterns",
    description:
      "An overview of Higher-Order Components and the classic separation of concerns pattern.",
    content: `
##  React Architecture Patterns
### 1. Higher-Order Components (HOC)
A **Higher-Order Component** is a function that takes a component as an argument and returns a new component with enhanced props or behavior.
- Mechanism: It’s a form of component logic reuse. The HOC wraps the input component.
- Syntax: \`const EnhancedComponent = withData(WrappedComponent);\`
- Example: A common HOC is one that manages loading state or authentication.
\`\`\`jsx
function withLoading(WrappedComponent) {
  return function WithLoading({ isLoading, ...props }) {
    if (isLoading) return <div>Loading...</div>;
    return <WrappedComponent {...props} />;
  };
}

const PostListWithLoading = withLoading(PostList);
\`\`\`
### 2. Container vs. Presentational (Smart/Dumb)
This is an older, class-component pattern that is largely achieved today using Custom Hooks, but the concept is still valuable for separation of concerns.
| Component Type | Role | Concerns |
| :--- | :--- | :--- |
| Container (Smart) | Manages data and logic. | Fetches data, manages state, handles business logic. |
| Presentational (Dumb) | Manages how things look. | Receives data via props, renders UI, only fires callbacks. |
**Modern Equivalent:** Today, **Custom Hooks** contain the logic/data fetching (Container logic), and the functional component remains a simple renderer (Presentational logic).
    `,
    tags: ["React", "Patterns", "HOC", "Container", "Presentational"],
    date: "2024-10-21",
  },
  {
    id: 142,
    topic: "react",
    seriesKey: "react",
    title: "React Ecosystem: Next.js",
    description:
      "Introduction to Next.js, the most popular full-stack React framework for server-side rendering and static generation.",
    content: `
##  React Ecosystem: Next.js
**Next.js** is a production-ready, open-source React framework that enables developers to build full-stack web applications with features like server-side rendering (SSR) and static site generation (SSG).
### Key Features:
- File-System Routing: Routes are created automatically based on the structure of the \`pages\` directory.
- API Routes: Allows you to build a Node.js backend API within your frontend project.
- Image Optimization: Built-in \`<Image>\` component optimizes images automatically.
### Rendering Methods:
| Method | Acronym | Description | Use Case |
| :--- | :--- | :--- | :--- |
| Server-Side Rendering | SSR | Renders the page on the server for every request. | Highly dynamic pages, frequently changing data. |
| Static Site Generation | SSG | Renders the page once at build time. | Marketing pages, blogs, and content that rarely changes. |
| Incremental Static Regeneration | ISR | A hybrid method that re-generates static pages in the background. | Static blogs with occasional updates. |
    `,
    tags: ["React", "Ecosystem", "Next.js", "SSR", "SSG", "Framework"],
    date: "2024-10-21",
  },
  {
    id: 143,
    topic: "react",
    seriesKey: "react",
    title: "React Ecosystem: React Native",
    description:
      "Overview of React Native for building truly native mobile applications using the React paradigm.",
    content: `
## React Ecosystem: React Native
**React Native** is a framework that allows you to build native mobile apps for iOS and Android using JavaScript and React.
### How it Works:
- No WebViews: Unlike traditional hybrid frameworks, React Native does not use WebViews. It uses JavaScript to communicate with the native platform (iOS/Android) and render native UI components.
- The Bridge: The communication happens over a "bridge," allowing the JavaScript thread to send instructions to the native UI thread.
### Key Characteristics:
- Code Reusability: Share a high percentage of non-UI logic between web and mobile apps.
- Native Experience: Apps look and perform like true native applications.
- Different Components: You use different components than React DOM (e.g., \`<View>\` instead of \`<div>\`, \`<Text>\` instead of \`<p>\`).
\`\`\`jsx
import { View, Text } from 'react-native';

const Welcome = () => {
  return (
    <View>
      <Text>Hello Mobile World</Text>
    </View>
  );
}
\`\`\`
    `,
    tags: ["React", "Ecosystem", "React Native", "Mobile", "Native"],
    date: "2024-10-22",
  },
  {
    id: 144,
    topic: "react",
    seriesKey: "react",
    title: "React Ecosystem: Storybook",
    description:
      "Introduction to Storybook for developing, documenting, and testing UI components in isolation.",
    content: `
##  React Ecosystem: Storybook
**Storybook** is a development environment for UI components. It allows you to build components in isolation, visualize their various states, and document them for future use.
### Purpose:
 **Isolation:** Components are developed and tested without needing to run the main application.
**Visual Testing:** You can visually test every possible state of a component (e.g., button: default, hover, disabled, loading).
 **Documentation:** It creates an interactive, searchable catalog of all your components.
 **Team Collaboration:** Designers, developers, and product managers can easily view and collaborate on the design system.
### Key Concept: Stories
A **Story** is a function that renders a component in a specific, predefined state.
\`\`\`javascript
// Button.stories.js
import { Button } from './Button';

export default {
  title: 'Components/Button',
  component: Button,
};

// A story showing the primary state
export const Primary = {
  args: {
    label: 'Primary Button',
    variant: 'primary',
    onClick: () => console.log('Clicked')
  },
};
\`\`\`
    `,
    tags: [
      "React",
      "Ecosystem",
      "Storybook",
      "UI Development",
      "Documentation",
    ],
    date: "2024-10-22",
  },
];
